import { useCallback, useEffect, useRef, useState } from 'react';
import { API_URL } from '../config/apiConfig';

/** After season end, server rollover may lag (~60s). Retry briefly, then stop. */
const ROLLOVER_RETRY_MS = 5000;
const ROLLOVER_RETRY_MAX_ATTEMPTS = 24; // ~2 minutes

/**
 * Pending season-end dialog for an authenticated in-game session.
 * Checks once on enable; after a season-end signal, retries briefly until the
 * result appears (or attempts exhaust). No month-long polling.
 *
 * @param {{ enabled: boolean }} options
 */
export function usePendingSeasonEnd({ enabled } = { enabled: false }) {
  const [pendingResult, setPendingResult] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const hasOpenDialogRef = useRef(false);
  const retryTimerRef = useRef(null);
  const retryAttemptsRef = useRef(0);

  const clearRetry = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    retryAttemptsRef.current = 0;
  }, []);

  const applyPending = useCallback((pending) => {
    if (!pending) {
      throw new Error('usePendingSeasonEnd.applyPending: pending result is required');
    }
    clearRetry();
    setPendingResult(pending);
    setDialogOpen(true);
    hasOpenDialogRef.current = true;
  }, [clearRetry]);

  const checkPending = useCallback(async () => {
    if (!enabled) return null;
    if (hasOpenDialogRef.current) return null;
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const res = await fetch(`${API_URL}/honor-hall/season/pending-end`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.pending) {
        applyPending(data.pending);
        return data.pending;
      }
      return null;
    } catch (err) {
      console.error('usePendingSeasonEnd check failed', err);
      return null;
    }
  }, [applyPending, enabled]);

  /**
   * player1-only: after live end timer, ensure a pending result exists even with no season activity.
   * @returns {Promise<{ pending: object|null, notEnded?: boolean }>}
   */
  const ensureDevPendingIfEnded = useCallback(async () => {
    if (!enabled || hasOpenDialogRef.current) {
      return { pending: null };
    }
    const token = localStorage.getItem('token');
    if (!token) {
      return { pending: null };
    }
    try {
      const res = await fetch(`${API_URL}/honor-hall/dev-season/ensure-pending-if-ended`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (res.status === 403) {
        return { pending: null };
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        console.error(
          'usePendingSeasonEnd ensure-pending-if-ended failed',
          errBody.error || res.status
        );
        return { pending: null };
      }
      const data = await res.json();
      if (data.reason === 'not_ended' || data.reason === 'ended_too_long_ago' || data.reason === 'no_completed_season') {
        return { pending: null, notEnded: true };
      }
      if (data.pendingSeasonEnd) {
        applyPending(data.pendingSeasonEnd);
        return { pending: data.pendingSeasonEnd };
      }
      return { pending: null };
    } catch (err) {
      console.error('usePendingSeasonEnd ensure-pending-if-ended failed', err);
      return { pending: null };
    }
  }, [applyPending, enabled]);

  const startRolloverRetry = useCallback(() => {
    clearRetry();
    const attempt = async () => {
      if (!enabled || hasOpenDialogRef.current) {
        clearRetry();
        return;
      }
      let pending = await checkPending();
      if (!pending) {
        const ensureResult = await ensureDevPendingIfEnded();
        if (ensureResult.notEnded) {
          // Clock was jumped back (e.g. another dev button); stop the end-retry loop.
          clearRetry();
          return;
        }
        pending = ensureResult.pending;
      }
      if (pending) {
        return;
      }
      retryAttemptsRef.current += 1;
      if (retryAttemptsRef.current >= ROLLOVER_RETRY_MAX_ATTEMPTS) {
        clearRetry();
        return;
      }
      retryTimerRef.current = setTimeout(attempt, ROLLOVER_RETRY_MS);
    };
    attempt();
  }, [enabled, checkPending, ensureDevPendingIfEnded, clearRetry]);

  const dismissDialog = useCallback(() => {
    setDialogOpen(false);
    setPendingResult(null);
    hasOpenDialogRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setDialogOpen(false);
      setPendingResult(null);
      hasOpenDialogRef.current = false;
      clearRetry();
      return undefined;
    }

    checkPending();

    const checkHandler = () => {
      startRolloverRetry();
    };
    const directHandler = (event) => {
      if (event?.detail) {
        applyPending(event.detail);
      }
    };
    const onDevSeasonChanged = (event) => {
      // Time-jump / start: cancel any in-flight end-retry from a previous arm.
      if (event?.detail?.armTimersOnly === true || event?.detail?.secondsUntilEnd > 0) {
        clearRetry();
      }
    };
    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      checkPending();
    };

    window.addEventListener('coda-check-pending-season-end', checkHandler);
    window.addEventListener('coda-pending-season-end', directHandler);
    window.addEventListener('coda-dev-season-changed', onDevSeasonChanged);
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    return () => {
      clearRetry();
      window.removeEventListener('coda-check-pending-season-end', checkHandler);
      window.removeEventListener('coda-pending-season-end', directHandler);
      window.removeEventListener('coda-dev-season-changed', onDevSeasonChanged);
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [enabled, checkPending, applyPending, startRolloverRetry, clearRetry]);

  return {
    pendingResult,
    dialogOpen: enabled && dialogOpen,
    checkPending,
    dismissDialog,
  };
}

export default usePendingSeasonEnd;
